import { useMemo, useState, type ReactNode } from "react";
import { DateRange } from "react-day-picker";
import { format, parseISO } from "date-fns";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { CalendarIcon, AlertCircle, Info, Loader2, ArrowUp, ArrowDown, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { OrdersTable } from "@/components/OrdersTable";
import { useWaitlistSnapshots, useWaitlistSnapshotOrders } from "@/hooks/useWaitlistSnapshots";
import {
  buildHistoryGrid,
  historyStoreIds,
  isoDateRange,
  clampIsoRange,
  MAX_HISTORY_SPAN_DAYS,
  presetIsoRange,
  todayInLA,
  sortHistoryRows,
  sortKeysEqual,
  resolveHistorySort,
  DEFAULT_HISTORY_SORT,
  type HistoryCell,
  type HistorySort,
  type HistorySortKey,
  type WaitlistSnapshot,
  chartSeriesLabels,
} from "@/utils/orderSpyHistory";
import type { SortDir } from "@/utils/orderSpySort";
import { EMPTY_CELL, fmtTime } from "@/utils/orderSpyFormat";
import type { PosStore } from "@/utils/posStores";
import { chartColors, brandSeriesStyle } from "@/lib/chartColors";
import { BrandTag } from "@/components/BrandTag";

// A "YYYY-MM-DD" business date as a local-calendar Date for the picker/format
// helpers (the date part is all that matters; no zone conversion happens).
const fromIsoDate = (iso: string): Date => parseISO(iso);
const toIsoDate = (d: Date): string => format(d, "yyyy-MM-dd");

// Presets in LA business days ending on today's LA date (the default is the last 7).
const PRESETS: { key: string; label: string; days: number }[] = [
  { key: "7d", label: "Last 7 days", days: 7 },
  { key: "14d", label: "Last 14 days", days: 14 },
  { key: "30d", label: "Last 30 days", days: 30 },
];

function presetRange(days: number): DateRange {
  const { from, to } = presetIsoRange(days);
  return { from: fromIsoDate(from), to: fromIsoDate(to) };
}

interface SelectedCell {
  storeId: string;
  storeLabel: string;
  businessDate: string;
  snapshot: WaitlistSnapshot;
}

export function OrderSpyHistory({
  pinnedIds,
  stores,
}: {
  pinnedIds: readonly string[];
  stores: readonly PosStore[];
}) {
  const [range, setRange] = useState<DateRange>(() => presetRange(7));
  const [activePreset, setActivePreset] = useState<string | null>("7d");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selected, setSelected] = useState<SelectedCell | null>(null);
  const [sort, setSort] = useState<HistorySort>(DEFAULT_HISTORY_SORT);

  const from = useMemo(() => range.from ?? presetRange(7).from!, [range.from]);
  const to = useMemo(() => range.to ?? range.from ?? fromIsoDate(todayInLA()), [range.to, range.from]);
  const fromIso = toIsoDate(from);
  const toIso = toIsoDate(to);

  const { data: snapshots = [], isLoading, error } = useWaitlistSnapshots(fromIso, toIso);
  const {
    data: selectedOrders,
    isLoading: ordersLoading,
    error: ordersError,
  } = useWaitlistSnapshotOrders(selected && !selected.snapshot.error ? selected.snapshot.id : null);

  // Store id -> display label, from the live registry (falls back to the id).
  const labelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of stores) map.set(s.store_id, s.location_name || s.store_id);
    return map;
  }, [stores]);
  const labelFor = (id: string) => labelById.get(id) ?? id;

  // Store id -> brand, for the per-brand colour (missing id falls back to the
  // neutral swatch via brandSwatch("")).
  const brandById = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of stores) map.set(s.store_id, s.brand);
    return map;
  }, [stores]);
  const brandFor = (id: string) => brandById.get(id) ?? "";

  const businessDates = useMemo(() => isoDateRange(fromIso, toIso), [fromIso, toIso]);

  const storeIds = useMemo(
    () => historyStoreIds([...pinnedIds], snapshots),
    [pinnedIds, snapshots],
  );

  // Legend: location text (brand carried by the line colour), disambiguated
  // with the brand only when two selected stores share a location. Tooltip:
  // always "Location · Brand".
  const seriesLabels = useMemo(
    () =>
      chartSeriesLabels(
        storeIds.map((id) => ({
          storeId: id,
          location: labelById.get(id) ?? id,
          brand: brandById.get(id) ?? "",
        })),
      ),
    [storeIds, labelById, brandById],
  );
  const legendLabel = (id: string) => seriesLabels.get(id)?.legend ?? labelFor(id);
  const tooltipLabel = (id: string) => seriesLabels.get(id)?.tooltip ?? labelFor(id);

  const grid = useMemo(
    () => buildHistoryGrid(snapshots, storeIds, businessDates),
    [snapshots, storeIds, businessDates],
  );

  const { key: sortKey, dir: sortDir } = useMemo(
    () => resolveHistorySort(sort, businessDates),
    [sort, businessDates],
  );

  const sortedRows = useMemo(
    () => sortHistoryRows(grid.rows, sort, businessDates, (id) => labelById.get(id) ?? id),
    [grid.rows, sort, businessDates, labelById],
  );

  const onSortClick = (key: HistorySortKey, defaultDir: SortDir) => {
    setSort(
      sortKeysEqual(sortKey, key)
        ? { key, dir: sortDir === "asc" ? "desc" : "asc" }
        : { key, dir: defaultDir },
    );
  };

  const isSortedBy = (key: HistorySortKey) => sortKeysEqual(sortKey, key);
  const ariaSort = (key: HistorySortKey) =>
    isSortedBy(key) ? (sortDir === "asc" ? "ascending" : "descending") : undefined;

  // One point per business date; each store is a series keyed by its store_id so
  // duplicate location names never collide. A missing/failed day is null (gap).
  const chartData = useMemo(
    () =>
      businessDates.map((date, colIndex) => {
        const point: Record<string, string | number | null> = { date };
        for (const row of grid.rows) {
          const cell = row.cells[colIndex];
          point[row.storeId] = cell && !cell.failed ? cell.orderCount : null;
        }
        return point;
      }),
    [businessDates, grid],
  );

  // Position of each row within its own brand (0-indexed, in row order), so
  // multiple pinned stores of the same brand get distinguishable chart lines.
  const indexWithinBrand = useMemo(() => {
    const seen = new Map<string, number>();
    const out = new Map<string, number>();
    for (const row of grid.rows) {
      const brand = brandById.get(row.storeId) ?? "";
      const i = seen.get(brand) ?? 0;
      out.set(row.storeId, i);
      seen.set(brand, i + 1);
    }
    return out;
  }, [grid.rows, brandById]);

  const applyPreset = (key: string, days: number) => {
    setRange(presetRange(days));
    setActivePreset(key);
  };

  const hasAnyData = snapshots.length > 0;

  return (
    <div className="space-y-6">
      {/* Range controls */}
      <Card>
        <CardContent className="p-4 sm:p-6 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            {PRESETS.map((p) => (
              <Button
                key={p.key}
                type="button"
                size="sm"
                variant={activePreset === p.key ? "default" : "outline"}
                className={
                  activePreset === p.key
                    ? "bg-action-primary hover:bg-action-primary/90 text-primary-foreground"
                    : ""
                }
                onClick={() => applyPreset(p.key, p.days)}
              >
                {p.label}
              </Button>
            ))}
            <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  variant={activePreset === null ? "default" : "outline"}
                  className={`flex items-center gap-2 ${
                    activePreset === null
                      ? "bg-action-primary hover:bg-action-primary/90 text-primary-foreground"
                      : ""
                  }`}
                >
                  <CalendarIcon className="w-4 h-4" />
                  {format(from, "MMM d")} – {format(to, "MMM d, yyyy")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="range"
                  defaultMonth={from}
                  selected={range}
                  numberOfMonths={2}
                  disabled={{ after: fromIsoDate(todayInLA()) }}
                  onSelect={(r) => {
                    if (!r?.from) return;
                    let next: DateRange = r;
                    if (r.to) {
                      const anchor = range.from && toIsoDate(r.from) === toIsoDate(range.from) ? "to" : "from";
                      const clamped = clampIsoRange(toIsoDate(r.from), toIsoDate(r.to), anchor);
                      next = { from: fromIsoDate(clamped.from), to: fromIsoDate(clamped.to) };
                    }
                    setRange(next);
                    setActivePreset(null);
                    if (next.to) setPickerOpen(false);
                  }}
                />
              </PopoverContent>
            </Popover>
            <span className="text-xs text-muted-foreground">Up to {MAX_HISTORY_SPAN_DAYS} days.</span>
          </div>
          <p className="text-xs text-muted-foreground">
            Business date is the store's Los Angeles calendar day. The nightly capture runs at
            11:50&nbsp;PM Pacific; the value shown is that scheduled capture when present, otherwise
            the latest capture that day.
          </p>
        </CardContent>
      </Card>

      {error && (
        <div
          role="status"
          className="flex items-center gap-2 rounded-md border border-status-danger/30 bg-status-danger/10 px-4 py-3 text-sm text-status-danger"
        >
          <AlertCircle className="w-4 h-4 flex-none" />
          Couldn't load history: {error instanceof Error ? error.message : "unknown error"}
        </div>
      )}

      {isLoading ? (
        <Card>
          <CardContent className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading saved captures…
          </CardContent>
        </Card>
      ) : storeIds.length === 0 || !hasAnyData ? (
        <Card>
          <CardContent className="flex items-start gap-2 p-6 text-sm text-muted-foreground">
            <Info className="w-4 h-4 flex-none mt-0.5" />
            <p>
              No saved captures in this range yet. Pin the stores you want to track, then use{" "}
              <span className="font-medium text-foreground">Save snapshot now</span> on the Live tab
              or wait for tonight's automatic capture.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Comparison table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead
                        className="sticky left-0 bg-card"
                        aria-sort={ariaSort({ column: "store" })}
                      >
                        <HistorySortButton
                          label="Store"
                          active={isSortedBy({ column: "store" })}
                          dir={sortDir}
                          align="left"
                          onClick={() => onSortClick({ column: "store" }, "asc")}
                        />
                      </TableHead>
                      {businessDates.map((d) => {
                        const key: HistorySortKey = { column: "date", date: d };
                        return (
                          <TableHead
                            key={d}
                            className="text-right tabular-nums whitespace-nowrap"
                            aria-sort={ariaSort(key)}
                          >
                            <HistorySortButton
                              label={
                                <span className="flex flex-col items-end leading-tight">
                                  <span className="text-[10px] font-normal text-muted-foreground">
                                    {format(parseISO(d), "EEE")}
                                  </span>
                                  <span>{format(parseISO(d), "MMM d")}</span>
                                </span>
                              }
                              active={isSortedBy(key)}
                              dir={sortDir}
                              align="right"
                              onClick={() => onSortClick(key, "desc")}
                            />
                          </TableHead>
                        );
                      })}
                      <TableHead
                        className="text-right font-semibold"
                        aria-sort={ariaSort({ column: "total" })}
                      >
                        <HistorySortButton
                          label="Total"
                          active={isSortedBy({ column: "total" })}
                          dir={sortDir}
                          align="right"
                          onClick={() => onSortClick({ column: "total" }, "desc")}
                        />
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedRows.map((row) => (
                      <TableRow key={row.storeId}>
                        <TableCell className="sticky left-0 bg-card font-medium max-w-[16rem]">
                          <BrandTag
                            brand={brandFor(row.storeId)}
                            label={labelFor(row.storeId)}
                            size="xs"
                            className="mb-1 max-w-full"
                          />
                          <span className="block font-mono text-xs text-muted-foreground tabular-nums">
                            {row.storeId}
                          </span>
                        </TableCell>
                        {row.cells.map((cell, i) => (
                          <TableCell key={businessDates[i]} className="text-right tabular-nums">
                            <HistoryGridCell
                              cell={cell}
                              onOpen={() =>
                                cell &&
                                setSelected({
                                  storeId: row.storeId,
                                  storeLabel: labelFor(row.storeId),
                                  businessDate: businessDates[i],
                                  snapshot: cell.snapshot,
                                })
                              }
                            />
                          </TableCell>
                        ))}
                        <TableCell className="text-right font-semibold tabular-nums">
                          {row.total}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TableCell className="sticky left-0 bg-muted font-semibold">All pinned</TableCell>
                      {grid.dateTotals.map((total, i) => (
                        <TableCell
                          key={businessDates[i]}
                          className="text-right font-semibold tabular-nums"
                        >
                          {total}
                        </TableCell>
                      ))}
                      <TableCell className="text-right font-bold tabular-nums">
                        {grid.grandTotal}
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Trend chart */}
          <Card>
            <CardContent className="p-4 sm:p-6">
              <h3 className="text-sm font-bold tracking-wide uppercase text-muted-foreground mb-3">
                Orders per store over time
              </h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11 }}
                      tickFormatter={(d: string) => format(parseISO(d), "EEE M/d")}
                    />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip
                      labelFormatter={(d: string) => format(parseISO(d), "EEEE, MMM d, yyyy")}
                      formatter={(value: number, storeId: string) => [
                        value,
                        tooltipLabel(storeId),
                      ]}
                    />
                    <Legend formatter={(storeId: string) => legendLabel(storeId)} />
                    {grid.rows.map((row) => {
                      const style = brandSeriesStyle(brandFor(row.storeId), indexWithinBrand.get(row.storeId) ?? 0);
                      return (
                        <Line
                          key={row.storeId}
                          type="monotone"
                          dataKey={row.storeId}
                          name={row.storeId}
                          stroke={style.stroke}
                          strokeDasharray={style.strokeDasharray}
                          strokeWidth={2}
                          dot={{ r: 2 }}
                          connectNulls={false}
                        />
                      );
                    })}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Cell detail: the captured order list */}
      <Dialog open={selected !== null} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-3xl">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="truncate">{selected.storeLabel}</DialogTitle>
                <DialogDescription>
                  {format(parseISO(selected.businessDate), "EEEE, MMM d, yyyy")} ·{" "}
                  {selected.snapshot.source} capture · saved {fmtTime(selected.snapshot.captured_at)}
                </DialogDescription>
              </DialogHeader>
              {selected.snapshot.error ? (
                <div className="flex items-start gap-2 rounded-md border border-status-danger/30 bg-status-danger/10 px-4 py-3 text-sm text-status-danger">
                  <AlertCircle className="w-4 h-4 flex-none mt-0.5" />
                  <span>This capture failed: {selected.snapshot.error}</span>
                </div>
              ) : ordersError ? (
                <div className="flex items-start gap-2 rounded-md border border-status-danger/30 bg-status-danger/10 px-4 py-3 text-sm text-status-danger">
                  <AlertCircle className="w-4 h-4 flex-none mt-0.5" />
                  <span>
                    Couldn't load this capture's orders:{" "}
                    {ordersError instanceof Error ? ordersError.message : "unknown error"}
                  </span>
                </div>
              ) : ordersLoading || !selectedOrders ? (
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading orders…
                </p>
              ) : selectedOrders.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No orders were on the waitlist for this capture.
                </p>
              ) : (
                <div className="max-h-[60vh] overflow-y-auto">
                  <OrdersTable orders={selectedOrders} />
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** One comparison-table column header: a clickable label + sort direction icon,
 *  matching the pattern in OrdersTable. */
function HistorySortButton({
  label,
  active,
  dir,
  align,
  onClick,
}: {
  label: ReactNode;
  active: boolean;
  dir: SortDir;
  align: "left" | "right";
  onClick: () => void;
}) {
  const Icon = active ? (dir === "asc" ? ArrowUp : ArrowDown) : ChevronsUpDown;
  return (
    <button
      type="button"
      className={`inline-flex items-center gap-1 hover:text-foreground ${
        align === "right" ? "flex-row-reverse" : ""
      } ${active ? "text-foreground font-semibold" : ""}`}
      onClick={onClick}
    >
      {label}
      <Icon className="w-3.5 h-3.5 flex-none" />
    </button>
  );
}

/** One comparison-table cell: a clickable count, a marked failure, or an em-dash
 *  for a day with no capture. */
function HistoryGridCell({ cell, onOpen }: { cell: HistoryCell | null; onOpen: () => void }) {
  if (!cell) return <span className="text-muted-foreground">{EMPTY_CELL}</span>;

  const hint =
    cell.captureCount > 1 ? (
      <span className="ml-1 text-[10px] text-muted-foreground">({cell.captureCount})</span>
    ) : null;

  if (cell.failed) {
    return (
      <button
        type="button"
        onClick={onOpen}
        title={cell.error ?? "The POS fetch failed for this capture."}
        className="inline-flex items-center rounded-full bg-status-danger/15 px-2 py-0.5 text-xs font-semibold text-status-danger hover:bg-status-danger/25"
      >
        failed{hint}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      className="font-medium text-foreground hover:text-action-primary hover:underline"
      title="View this capture's orders"
    >
      {cell.orderCount}
      {hint}
    </button>
  );
}

export default OrderSpyHistory;
